import {
  Box,
  Button,
  HStack,
  IconButton,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import RefreshSharpIcon from '@gamma/ui/icons/RefreshSharpIcon';
import { MsDefenderMark } from '@gamma/ui/logos';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import moment from 'moment';
import { useCallback, useContext, useMemo } from 'react';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { MISSING_DATA_PLACEHOLDER } from '../../../../../shared/constants';
import { EntityPanelRow } from '@investigator/features/detections/components';
import { EntityEdrStatus } from '../../../queries';
import { MicrosoftDefenderEndpointDevice } from '@investigator/shared/generated/graphql';
import { ChangeStatusModal } from '../ChangeStatusModal/ChangeStatusModal';
import { useEntitiesContext, useEntityEdrContext } from '../../../context';

const { entityEDR } = i18n.pages.detections;
const { edrAction } = entityEDR;

interface MSDefenderModalSectionProps {
  data: MicrosoftDefenderEndpointDevice;
}

export const MSDefenderModalSection = ({
  data,
}: MSDefenderModalSectionProps): JSX.Element => {
  const { selectedEntity } = useEntitiesContext();
  const { defenderStatus, performEdrAction, performEdrActionLoading } =
    useEntityEdrContext();

  const { userRole, userPrivileges } = useContext(AuthContext);

  const { isOpen, onClose, onOpen } = useDisclosure();

  const { entityName, tenantEntity, tenantInfo, detection_id } =
    selectedEntity || {};
  const { tenant_id = '' } = tenantInfo || {};

  const edrActionEnabled =
    userPrivileges?.[tenant_id ?? '']?.microsoft_defender_action_enabled;

  const { id, machine, entity_id, last_updated_timestamp } = data;

  const {
    riskScore,
    osPlatform,
    version,
    osProcessor,
    ipAddresses,
    healthStatus,
    managedBy,
    managedByStatus,
    computerDnsName,
    firstSeen: edrFirstSeen,
    lastSeen: edrLastSeen,
    lastExternalIpAddress,
  } = machine || {};

  const mac_address = ipAddresses?.find(
    (ip) => ip?.ipAddress === entityName,
  )?.macAddress;

  // Get status from context
  const { entity_edr_status } = defenderStatus.data || {};
  const getEntityEdrStatusLoading = defenderStatus.loading ?? true;
  const pullEntityEdrStatusLoading = defenderStatus.refreshLoading ?? true;

  const edrStatusAlert = useMemo(() => {
    if (entity_edr_status) {
      switch (entity_edr_status) {
        case EntityEdrStatus.Normal:
          return {
            colorScheme: 'green',
            statusText: edrAction.normal,
          };
        case EntityEdrStatus.Isolated:
          return {
            colorScheme: 'red',
            statusText: edrAction.isolated,
          };
        case EntityEdrStatus.IsolationPending:
          return {
            colorScheme: 'yellow',
            statusText: edrAction.pendingIsolation,
          };
        case EntityEdrStatus.LiftIsolationPending:
          return {
            colorScheme: 'yellow',
            statusText: edrAction.pendingLift,
          };
      }
    }
    return undefined;
  }, [entity_edr_status]);

  // Handle refresh using context
  const handleRefreshStatus = useCallback(() => {
    if (tenantInfo && tenantEntity) {
      defenderStatus.refresh();
    }
  }, [tenantInfo, tenantEntity, defenderStatus]);

  // Handle status change action
  const handleStatusChange = useCallback(async () => {
    const action =
      entity_edr_status === EntityEdrStatus.Isolated
        ? 'lift_isolation'
        : 'isolate';
    await performEdrAction({
      entityId: entity_id ?? '',
      action,
      tenant: tenant_id ?? '',
      detectionId: detection_id,
    });
    onClose();
  }, [
    entity_edr_status,
    performEdrAction,
    entity_id,
    tenant_id,
    detection_id,
    onClose,
  ]);

  const pendingStateTooltip = useMemo(() => {
    if (
      entity_edr_status === EntityEdrStatus.IsolationPending ||
      entity_edr_status === EntityEdrStatus.LiftIsolationPending
    ) {
      return edrAction.pendingStateTooltip;
    }
    return undefined;
  }, [entity_edr_status]);

  return (
    <VStack w="100%" alignItems="start">
      <HStack data-testid="msdefender-header">
        <MsDefenderMark />
        <Text textStyle="h5">Microsoft Defender</Text>
      </HStack>
      <HStack w="100%" spacing={4} alignItems="start">
        <VStack w="354px">
          <EntityPanelRow
            heading={edrAction.entityStatus}
            data-testid="msdefender-entity-status"
            hasTestIdValue={false}
          >
            <HStack>
              {getEntityEdrStatusLoading ? (
                <GraphQLReqStatus
                  loading={getEntityEdrStatusLoading}
                  spinnerSize="xs"
                />
              ) : edrStatusAlert ? (
                <HStack spacing={0}>
                  <Tooltip label={pendingStateTooltip} placement="top" hasArrow>
                    <Tag
                      size="md"
                      variant="solid"
                      textAlign="center"
                      colorScheme={edrStatusAlert?.colorScheme}
                      data-testid="msdefender-entity-status-status-value"
                    >
                      {edrStatusAlert?.statusText}
                    </Tag>
                  </Tooltip>
                  <IconButton
                    data-testid={`msdefender-entity-status-refresh-value`}
                    size="sm"
                    aria-label={edrAction.refreshEntityStatus}
                    onClick={handleRefreshStatus}
                    isDisabled={
                      getEntityEdrStatusLoading ||
                      pullEntityEdrStatusLoading ||
                      userRole === 'viewer'
                    }
                    icon={
                      getEntityEdrStatusLoading ||
                      pullEntityEdrStatusLoading ? (
                        <Box w="20px" h="20px">
                          <Spinner size="xs" color="primary.500" />
                        </Box>
                      ) : (
                        <RefreshSharpIcon />
                      )
                    }
                  />
                  <Tooltip
                    w="120px"
                    placement="top"
                    label={
                      userRole !== 'admin'
                        ? edrAction.requestPermissions
                        : undefined
                    }
                  >
                    <Button
                      data-testid={`msdefender-entity-status-action-value`}
                      size="xs"
                      variant="solid"
                      colorScheme="gray"
                      onClick={() => onOpen()}
                      isDisabled={
                        userRole !== 'admin' ||
                        getEntityEdrStatusLoading ||
                        pullEntityEdrStatusLoading ||
                        !edrActionEnabled ||
                        entity_edr_status ===
                          EntityEdrStatus.IsolationPending ||
                        entity_edr_status ===
                          EntityEdrStatus.LiftIsolationPending
                      }
                    >
                      {entity_edr_status === EntityEdrStatus.Isolated
                        ? edrAction.liftIsolation
                        : edrAction.isolateEntity}
                    </Button>
                  </Tooltip>
                </HStack>
              ) : getEntityEdrStatusLoading ? (
                <Box w="20px" h="20px">
                  <Spinner size="xs" color="white" />
                </Box>
              ) : (
                <Text data-testid="msdefender-entity-status-status-value">
                  {MISSING_DATA_PLACEHOLDER}
                </Text>
              )}
            </HStack>
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.timestamp}
            data-testid="msdefender-timestamp"
          >
            {last_updated_timestamp
              ? moment.unix(last_updated_timestamp).format('MMMM D, YYYY h:mma')
              : undefined}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.macAddress}
            data-testid="msdefender-mac-address"
          >
            {mac_address}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.dnsName}
            data-testid="msdefender-dns-name"
          >
            {computerDnsName}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.platform}
            data-testid="msdefender-platform"
          >
            {osPlatform}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.osVersion}
            data-testid="msdefender-os-version"
          >
            {version}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.osProcessor}
            data-testid="msdefender-os-processor"
          >
            {osProcessor}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.externalIp}
            data-testid="msdefender-external-ip"
          >
            {lastExternalIpAddress}
          </EntityPanelRow>
          <EntityPanelRow
            disableBorders
            heading={entityEDR.deviceId}
            data-testid="msdefender-device-id"
          >
            {id}
          </EntityPanelRow>
        </VStack>
        <VStack>
          <EntityPanelRow
            heading={entityEDR.firstSeen}
            data-testid="msdefender-first-seen"
          >
            {edrFirstSeen
              ? moment.unix(edrFirstSeen).format('MMMM D, YYYY h:mma')
              : undefined}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.lastSeen}
            data-testid="msdefender-last-seen"
          >
            {edrLastSeen
              ? moment.unix(edrLastSeen).format('MMMM D, YYYY h:ma')
              : undefined}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.healthStatus}
            data-testid="msdefender-health-status"
          >
            {healthStatus}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.riskScore}
            data-testid="msdefender-risk-score"
          >
            {riskScore}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.managedBy}
            data-testid="msdefender-managed-by"
          >
            {managedBy}
          </EntityPanelRow>
          <EntityPanelRow
            heading={entityEDR.managedByStatus}
            data-testid="msdefender-managed-by-status"
          >
            {managedByStatus}
          </EntityPanelRow>
        </VStack>
      </HStack>
      <ChangeStatusModal
        isOpen={isOpen}
        onClose={onClose}
        entityId={entity_id ?? ''}
        tenant={tenant_id ?? ''}
        entityName={entityName}
        detectionId={detection_id ?? ''}
        action={
          entity_edr_status === EntityEdrStatus.Isolated
            ? 'lift_isolation'
            : 'isolate'
        }
        platform="Microsoft Defender"
        onConfirm={handleStatusChange}
        isLoading={performEdrActionLoading}
      />
    </VStack>
  );
};
